import { useTranslation } from 'react-i18next';
import {
    CardContent,
    Typography,
    Icon,
    useTheme,
    PargodaRoundButton,
    useAppDispatch,
    useAppSelector,
} from '../../../../base';
import { ICandidateJob, JobAttribute } from '../../../../common';
import {
    StyledCard,
    CardActions,
    JobInfo,
    StyledIconButton,
} from './JobCard.styles';
import { jobsActions, jobsSelectors } from '../../../../actions';
import ShareJobModal from '../../../../components/ShareJobModal';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RoutePaths } from '../../../../routes/RoutePaths';

interface Props {
    job: ICandidateJob;
}
export const JobCard = ({ job }: Props) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const candidateSavedJobs = useAppSelector(
        jobsSelectors.getCandidateSavedJobs
    );
    const dispatch = useAppDispatch();
    const [shareModalOpen, setShareModalOpen] = useState(false);

    const handleBookmarkClick = () => {
        let candidateSavedJobsIds = candidateSavedJobs;

        if (candidateSavedJobsIds.includes(String(job.id))) {
            // Remove the job ID from bookmarks
            candidateSavedJobsIds = candidateSavedJobsIds.filter(
                (id) => id !== String(job.id)
            );
        } else {
            candidateSavedJobsIds = [...candidateSavedJobsIds, String(job.id)];
        }

        dispatch(
            jobsActions.updateCandidateSavedJobs({
                jobIds: candidateSavedJobsIds,
            })
        );
    };

    const getJobAttributeValue = (
        job: ICandidateJob,
        attributeName: string
    ): string => {
        const attribute = job.jobAttributes?.find(
            (attr) => attr.jobAttributeOption.name === attributeName
        );

        return attribute?.jobAttributeValue.name || '';
    };

    const truncateDescription = (
        description: string | null
    ): JSX.Element | string => {
        if (!description) return '';

        // Strip HTML tags
        const strippedDescription = description.replace(/<[^>]*>/g, '');

        const maxLength = 120;
        const suffix = t('jobsBoard.viewMoreInfo');

        if (strippedDescription.length <= maxLength) {
            return strippedDescription;
        }

        // Find the last space before maxLength
        const truncateIndex = strippedDescription.lastIndexOf(
            ' ',
            maxLength - suffix.length
        );
        const truncatedText = strippedDescription.slice(0, truncateIndex);

        return (
            <>
                {truncatedText}...{' '}
                <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.75em' }}
                >
                    ({suffix})
                </Typography>
            </>
        );
    };

    const shareModal = () => {
        return (
            <ShareJobModal
                isModalOpen={shareModalOpen}
                setIsModalOpen={() => {
                    setShareModalOpen(false);
                }}
                modalTitle={t('positionPage.shareJob')}
                jobId={job.id}
            />
        );
    };

    return (
        <>
            <StyledCard>
                <CardContent
                    sx={{
                        padding: 2,
                        paddingTop: 1,
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'auto',
                    }}
                >
                    <Typography variant="h4" sx={{ paddingBottom: 0.5 }}>
                        {job.title}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                        {t('jobsBoard.published')}: {job.publishedDate}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, mb: 1 }}>
                        <strong>{t('jobsBoard.jobDescription')}: </strong>
                        {truncateDescription(job.description)}
                    </Typography>
                    <JobInfo>
                        <Typography
                            variant="body2"
                            color={theme.palette.grey[600]}
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                wordWrap: 'break-word',
                            }}
                        >
                            {job.location}
                        </Typography>
                        <Typography
                            variant="body2"
                            color={theme.palette.grey[600]}
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                wordWrap: 'break-word',
                            }}
                        >
                            {getJobAttributeValue(job, JobAttribute.JobType)}
                        </Typography>
                        <Typography
                            variant="body2"
                            color={theme.palette.grey[600]}
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                wordWrap: 'break-word',
                            }}
                        >
                            {t('jobsBoard.jobId')}: {job.id}
                        </Typography>
                    </JobInfo>
                </CardContent>
                <CardActions>
                    <div>
                        <StyledIconButton
                            onClick={handleBookmarkClick} // Use the handler
                            borderColor={theme.palette.secondary.dark}
                        >
                            <Icon
                                name={
                                    candidateSavedJobs.includes(String(job.id))
                                        ? 'BookmarkFilledIcon'
                                        : 'BookmarkButtonIcon'
                                }
                                color={theme.palette.secondary.dark}
                            />
                        </StyledIconButton>
                        <StyledIconButton
                            onClick={() => {
                                setShareModalOpen(true);
                            }}
                            size="small"
                            borderColor={theme.palette.grey[600]}
                            iconColor={theme.palette.grey[600]}
                        >
                            <Icon name={'ShareButtonIcon'} size={40} />
                        </StyledIconButton>
                    </div>
                    <PargodaRoundButton
                        variant="outlined"
                        color="primary"
                        onClick={() => {
                            const queryParams = new URLSearchParams(
                                location.search
                            );
                            const companyKey = queryParams.get('ck') || '';
                            navigate(
                                `${RoutePaths.positions}/${job.id}/?ck=${companyKey}`
                            );
                        }}
                        sx={{
                            '&:hover': {
                                background: `linear-gradient(0deg,${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                color: theme.palette.common.white,
                            },
                        }}
                        buttonColor={''}
                    >
                        {t('jobsBoard.viewMore')}
                    </PargodaRoundButton>
                </CardActions>
            </StyledCard>
            {shareModal()}
        </>
    );
};
